import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  Fare,
  FareSliceDeparture,
  FareSliceOutbound,
  TripDetailsV2,
  TripSlice,
} from "@b2bportal/air-shopping-api";
import {
  FlightSearchSelectors,
  getFlightData,
  getTripDetails,
  getSelectedFareShelfRatings,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import { HtsAirCfarOffersSelectors } from "@b2bportal/core-flights-cfar-offers";
import type {
  AlgomerchTag,
  AppDispatch,
  IFareTrip,
  IFlightListData,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import type { IFareData, IFlightFares, TripCategory } from "@overrides/types";
import { getCheapestFilteredFare } from "@overrides/utilities";
import {
  type FlightCardSummaryComponentProps,
  getFareData,
} from "@tenant/flights";
import dayjs from "dayjs";
import {
  FILTERED_TAGS,
  FIXED_PRICING_FILTERED_TAGS,
  filterFareTags,
} from "./filterFareTags";
import { usePackageFlightCardFareData } from "@/pages/Packages/hooks";
import { getPricingMode } from "@/store/flights/shop/store/selectors";
import { fetchTripDetails } from "@/store/flights/shop/store/thunks";
import { createSyntheticFareData } from "@/utilities/flights/fare";

const DEFAULT_TAGS_LIMIT = 2;

export interface UseFlightCardParams {
  flight: IFlightListData<Fare | FareSliceOutbound | FareSliceDeparture>;
  isSelected: boolean;
  sliceIndex: number;
  onClick?: () => void;
}

export type UseFlightCardContext = {
  isFlightDetailsLoading: boolean;
  isHackerFare: boolean;
  open: boolean;
  flightCardSummary?: FlightCardSummaryComponentProps;
  fareData: IFlightFares;
  tripCategory: TripCategory;
  fareShelfFilter: Array<number>;
  flightDetails?: TripDetailsV2;
  flightSlice?: TripSlice;
  fareShelfFilterWithUpsell: Array<number>;
  openFlightDetailsDialog: boolean;
  hasSelfTransferLayover: boolean;
  flightFareTrips: IFareTrip[];
};

export interface UseFlightCardResponse {
  context: UseFlightCardContext;
  handlers: {
    handleFlightCardClick: () => void;
    setOpenFlightDetailsDialog: Dispatch<SetStateAction<boolean>>;
    handleFareGridSelection: (fare: IFareData) => void;
  };
}

export const useFlightCard = ({
  flight,
  isSelected,
  sliceIndex = 0,
  onClick,
}: UseFlightCardParams): UseFlightCardResponse => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, formatFiatCurrency, formatDateTime, DateTimeFormatStyle } =
    useI18nContext();

  const { sliceId, fares } = flight;

  // INTERNAL STATE
  const [fareGridFareShelfClick, setFareGridFareShelfClick] =
    useState<number>(0);
  const [openFlightDetailsDialog, setOpenFlightDetailsDialog] = useState(false);

  // Create a stable key for fare trips so downstream selectors don't recompute
  const fareTripsKey = useMemo(() => {
    const keys = flight.fares.map((fare: Fare | FareSliceOutbound) => {
      return "example" in fare
        ? `${fare.example.trip}:${fare.example.fare}`
        : `${fare.tripId}:${fare.id}`;
    });
    keys.sort();
    return keys.join("|");
  }, [flight.fares]);

  const flightFareTrips = useMemo(() => {
    const flightFares = flight.fares;
    return flightFares.map((fare: Fare | FareSliceOutbound) => {
      return "example" in fare
        ? { fareId: fare.example.fare, tripId: fare.example.trip }
        : { fareId: fare.id, tripId: fare.tripId };
    });
  }, [fareTripsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // STATE
  const isHackerFare = useSelector(FlightShopSelectors.isHackerFare);
  const flightData = useSelector((state) => getFlightData(state, sliceId));
  const fareData = useSelector((state) => getFareData(state, fares));
  const fareShelfFilter = useSelector(getSelectedFareShelfRatings);

  const { isLoading: isFlightDetailsLoading, data: flightDetails } =
    useSelector((state) => getTripDetails(state, flightFareTrips));

  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);
  const isFixedPricing = useSelector(getPricingMode) === "fixed";

  const shouldShowOnlySyntheticFares = useSelector(
    HtsAirCfarOffersSelectors.selectShouldShowOnlySyntheticFares
  );
  const returnSliceBatchCfarOffersData = useSelector(
    HtsAirCfarOffersSelectors.selectReturnSliceBatchCfarOffersData
  );
  // shouldShowOnlySyntheticFares only applies to the round-trip RETURN leg -
  // returnSliceBatchCfarOffersData is keyed by return-leg fare IDs, which
  // never match a departure flight's fares. Gate on sliceIndex so departure
  // cards keep their real fare data instead of being substituted down to an
  // empty object (which crashes getCheapestFilteredFare downstream).
  const isReturnLeg = sliceIndex !== 0;
  const baseResolvedFareData = useMemo(() => {
    const baseFareData =
      shouldShowOnlySyntheticFares && isReturnLeg
        ? createSyntheticFareData(fareData, returnSliceBatchCfarOffersData)
        : fareData;

    return filterFareTags(
      baseFareData,
      isFixedPricing ? FIXED_PRICING_FILTERED_TAGS : FILTERED_TAGS
    );
  }, [
    fareData,
    returnSliceBatchCfarOffersData,
    shouldShowOnlySyntheticFares,
    isReturnLeg,
    isFixedPricing,
  ]);

  // In the package funnel the flight shop fares carry flight-only amounts;
  // swap in the package price so the collapsed card summary (the only price
  // shown on mobile) reflects the package overage. No-op outside packages.
  const resolvedFareData = usePackageFlightCardFareData(baseResolvedFareData);

  // CHILD PROPS
  const flightCardSummary = useMemo(() => {
    if (flightData == null) return undefined;

    const cheapestFilteredFare = getCheapestFilteredFare(
      resolvedFareData,
      fareShelfFilter,
      shouldShowOnlySyntheticFares,
      returnSliceBatchCfarOffersData
    );

    const cheapestPrice =
      "taxesAndFees" in cheapestFilteredFare.price
        ? cheapestFilteredFare.price.taxesAndFees.fiat
        : cheapestFilteredFare.price.fiat;

    const getTags = (fares: IFlightFares) => {
      return Object.values(fares).reduce(
        (tags: AlgomerchTag[], fare: IFareData) => {
          return [...tags, ...(fare.tags ?? [])];
        },
        [] as AlgomerchTag[]
      );
    };

    const formatDuration = ({ days, hours, minutes }) => {
      const units = [
        { value: days, key: t("flightDuration.days", { count: days ?? 0 }) },
        { value: hours, key: t("flightDuration.hours", { count: hours ?? 0 }) },
        {
          value: minutes,
          key: t("flightDuration.minutes", { count: minutes ?? 0 }),
        },
      ];

      const durationString = units
        .filter((unit) => unit.value)
        .map((unit) => unit.key)
        .join(" ");

      return durationString.trim();
    };

    const uniqueMarketingAirlines = new Set(
      flightData.segments.map((segment) => segment.marketingAirline)
    );
    const additionalAirlines =
      uniqueMarketingAirlines.size > 0
        ? uniqueMarketingAirlines.size - 1
        : undefined;

    return {
      duration: formatDuration(flightData.duration),
      currentPriceText: formatFiatCurrency(cheapestPrice),
      currentPrice: cheapestFilteredFare.price,
      originCode: flightData.originCode,
      destinationCode: flightData.destinationCode,
      departureTime: formatDateTime(
        dayjs(removeTimezone(flightData.departureDateTime)).toDate(),
        DateTimeFormatStyle.ShortTime
      ),
      arrivalTime: formatDateTime(
        dayjs(removeTimezone(flightData.arrivalDateTime)).toDate(),
        DateTimeFormatStyle.ShortTime
      ),
      brandName: cheapestFilteredFare?.fareClass,
      fareName: cheapestFilteredFare?.fareName,
      primaryCarrier: flightData.airlineCode,
      airlineName: flightData.airlineName,
      tags: getTags(resolvedFareData),
      tagsLimit: DEFAULT_TAGS_LIMIT,
      flightStops: flightData.flightStops,
      additionalAirlines,
    };
  }, [
    flightData,
    resolvedFareData,
    fareShelfFilter,
    shouldShowOnlySyntheticFares,
    returnSliceBatchCfarOffersData,
    formatFiatCurrency,
    formatDateTime,
    DateTimeFormatStyle.ShortTime,
    t,
  ]);

  const fareShelfFilterWithUpsell = useMemo(() => {
    return fareShelfFilter.filter(
      (fareShelf) => fareShelf >= fareGridFareShelfClick
    );
  }, [fareShelfFilter, fareGridFareShelfClick]);

  const flightSlice = flightDetails?.slices[sliceIndex];

  // ON CLICKS
  const fetchFlightTripsDetails = useCallback(
    (fareTrips: IFareTrip[]) => {
      const uniqueTripIds: string[] = Array.from(
        new Set(fareTrips.map((fareTrip) => fareTrip.tripId))
      );
      uniqueTripIds.forEach((tripId: string) => {
        const relatedFareId = fareTrips.find(
          (fareTrip) => fareTrip.tripId === tripId
        )?.fareId;
        dispatch(fetchTripDetails({ tripId, fareId: relatedFareId }));
      });
    },

    [dispatch]
  );

  const handleFlightCardClick = useCallback(() => {
    if (!isSelected) {
      fetchFlightTripsDetails(flightFareTrips);
    }
    onClick?.();
  }, [flightFareTrips, fetchFlightTripsDetails, onClick, isSelected]);

  const handleFareGridSelection = useCallback(
    (fare: IFareData) => {
      // Set fare id or fare shelf and only upsell in fare details.
      setFareGridFareShelfClick(fare.fareShelf);
      handleFlightCardClick();
    },
    [handleFlightCardClick, setFareGridFareShelfClick]
  );

  return {
    context: {
      isFlightDetailsLoading,
      isHackerFare,
      open: isSelected,
      flightCardSummary,
      fareData: resolvedFareData,
      tripCategory,
      fareShelfFilter,
      flightDetails,
      flightSlice,
      fareShelfFilterWithUpsell,
      openFlightDetailsDialog,
      flightFareTrips,
      hasSelfTransferLayover: flightData?.hasSelfTransferLayover ?? false,
    },
    handlers: {
      handleFlightCardClick,
      setOpenFlightDetailsDialog,
      handleFareGridSelection,
    },
  };
};
